import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "badge inline-flex items-center justify-center rounded-full border px-2 py-0.5 paragraph-sm w-fit shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden",
  {
    variants: {
      variant: {
        default:
          "border-brand bg-transparent text-foreground [a&]:hover:bg-brand/90",
        primary:
          "border-brand bg-transparent text-foreground [a&]:hover:bg-brand/90",
        secondary:
          "border-gray-100 dark:border-gray-500 bg-gray-100 dark:bg-gray-500 text-primary [a&]:hover:bg-gray-100 dark:[a&]:hover:bg-gray-500",
        destructive:
          "border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
      },
      size: {
        sm: "px-2 py-0.5",
        md: "px-3 py-1",
        lg: "px-4 py-2",
      },
      bold: {
        true: "font-bold",
      },
      lifted: {
        true: "shadow-sm hover:shadow-md",
      },
      wrap: {
        true: "whitespace-normal",
        false: "whitespace-nowrap",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      bold: false,
      lifted: false,
      wrap: false,
    },
  }
)

function Badge({
  className,
  variant,
  size = "md",
  bold = false,
  lifted = false,
  wrap = false,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean; }) {
  const Comp = asChild ? Slot : "span"

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size, bold, lifted, wrap }), className)}
      {...props}
    />
  )
}

export { Badge, badgeVariants }
